import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  FormHelperText,
  Slider,
  Switch,
  FormControlLabel,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardMedia
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { getBaseURL } from '../../../../services/apiClient';
import { getToken } from '../../../../utils/jwtUtils';
import {
  PoolProblem,
  ProblemCategory,
  ProblemTag,
  createPoolProblem,
  updatePoolProblem,
  CreatePoolProblemData
} from '../../../../services/problemPoolService';

interface ProblemPoolFormDialogProps {
  open: boolean;
  onClose: () => void;
  problem: PoolProblem | null;
  isEditMode: boolean;
  categories: ProblemCategory[];
  tags: ProblemTag[];
  onSuccess: () => void;
}

interface FormData {
  title: string;
  problemStatement: string;
  correctSolution: string;
  difficultyLevel: number;
  categoryId: number | '';
  problemType: 'open_ended' | 'multiple_choice';
  source: string;
  notes: string;
  tagIds: number[];
  imagePath: string | null;
  options: { optionText: string; isCorrect: boolean; optionOrder: number }[];
}

const defaultValues: FormData = {
  title: '',
  problemStatement: '',
  correctSolution: '',
  difficultyLevel: 1,
  categoryId: '',
  problemType: 'open_ended',
  source: '',
  notes: '',
  tagIds: [],
  imagePath: null,
  options: [
    { optionText: '', isCorrect: true, optionOrder: 1 },
    { optionText: '', isCorrect: false, optionOrder: 2 },
    { optionText: '', isCorrect: false, optionOrder: 3 },
    { optionText: '', isCorrect: false, optionOrder: 4 }
  ]
};

function getImageS3Url(imagePath: string): string {
  if (imagePath.startsWith('http')) return imagePath;
  const filename = imagePath.split('/').pop();
  if (!filename) return '';
  const bucketName = process.env.REACT_APP_S3_BUCKET_NAME || 'mathwiz-images';
  const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
  return `https://${bucketName}.s3.${region}.amazonaws.com/uploads/${filename}`;
}

const ProblemPoolFormDialog: React.FC<ProblemPoolFormDialogProps> = ({
  open,
  onClose,
  problem,
  isEditMode,
  categories,
  tags,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Defer ReactQuill mount until Dialog transition completes.
  // Quill requires a fully-visible DOM container to initialize correctly;
  // mounting during the Fade animation produces an empty editor.
  const [editorReady, setEditorReady] = useState(false);

  const initialValues: FormData = isEditMode && problem ? {
    title: problem.title || '',
    problemStatement: problem.problemStatement || '',
    correctSolution: problem.correctSolution || '',
    difficultyLevel: problem.difficultyLevel || 1,
    categoryId: problem.categoryId || '',
    problemType: problem.problemType || 'open_ended',
    source: problem.source || '',
    notes: problem.notes || '',
    tagIds: problem.tags?.map(t => t.id) || [],
    imagePath: problem.imagePath || null,
    options: problem.options?.length
      ? problem.options.map(o => ({
          optionText: o.optionText,
          isCorrect: o.isCorrect,
          optionOrder: o.optionOrder
        }))
      : defaultValues.options
  } : defaultValues;

  const methods = useForm<FormData>({ defaultValues: initialValues });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = methods;

  const { fields: optionFields, append: appendOption, remove: removeOption } = useFieldArray({
    control,
    name: 'options'
  });

  const problemType = watch('problemType');
  const selectedTagIds = watch('tagIds');

  // Local state for ReactQuill editor content
  const [statementHtml, setStatementHtml] = useState(initialValues.problemStatement);

  // Sync editor content from props when problem data is available.
  // useState only captures the value at first mount — this effect covers
  // cases where the prop arrives after the initial render.
  useEffect(() => {
    if (isEditMode && problem?.problemStatement) {
      setStatementHtml(problem.problemStatement);
      setValue('problemStatement', problem.problemStatement);
    }
  }, [isEditMode, problem, setValue]);

  const handleStatementChange = useCallback(
    (content: string, _delta: unknown, source: string) => {
      if (source === 'user') {
        setStatementHtml(content);
        setValue('problemStatement', content, { shouldDirty: true });
      }
    },
    [setValue]
  );

  // Set image preview when dialog opens
  useEffect(() => {
    if (open) {
      if (isEditMode && problem?.imagePath) {
        setPreviewUrl(getImageS3Url(problem.imagePath));
      } else {
        setPreviewUrl(null);
      }
      setError(null);
      setUploadError(null);
    }
  }, [open, isEditMode, problem]);

  const handleTagToggle = (tagId: number) => {
    const current = selectedTagIds || [];
    if (current.includes(tagId)) {
      setValue('tagIds', current.filter(id => id !== tagId));
    } else {
      setValue('tagIds', [...current, tagId]);
    }
  };

  const handleCorrectOptionChange = (index: number) => {
    // Set only this option as correct
    optionFields.forEach((_, i) => {
      setValue(`options.${i}.isCorrect`, i === index);
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) return;
    const file = event.target.files[0];

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);

    try {
      setUploading(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post(
        `${getBaseURL()}/api/upload/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );

      setValue('imagePath', response.data.filePath);
    } catch (err) {
      let msg = 'Failed to upload image. ';
      if (axios.isAxiosError(err) && err.response) {
        msg += (err.response.data as { message?: string })?.message || err.message;
      } else if (err instanceof Error) {
        msg += err.message;
      }
      setUploadError(msg);
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setValue('imagePath', null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSubmit = async (data: FormData) => {
    // Validate problem statement (managed outside Controller via Quill ref)
    const statementText = (data.problemStatement || '').replace(/<[^>]*>/g, '').trim();
    if (!statementText) {
      setError('Problem statement is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData: CreatePoolProblemData = {
        title: data.title || undefined,
        problemStatement: data.problemStatement,
        difficultyLevel: data.difficultyLevel,
        problemType: data.problemType,
        categoryId: data.categoryId || undefined,
        source: data.source || undefined,
        notes: data.notes || undefined,
        tagIds: data.tagIds.length > 0 ? data.tagIds : undefined,
        imagePath: data.imagePath || undefined
      };

      if (data.problemType === 'open_ended') {
        submitData.correctSolution = data.correctSolution;
      } else {
        // For multiple choice, the correct answer is derived from options
        const correctOption = data.options.find(o => o.isCorrect);
        submitData.correctSolution = correctOption?.optionText || '';
        submitData.options = data.options.filter(o => o.optionText.trim());
      }

      if (isEditMode && problem) {
        // For updates, explicitly send imagePath: null to clear a removed image
        await updatePoolProblem(problem.id, {
          ...submitData,
          imagePath: data.imagePath
        });
      } else {
        await createPoolProblem(submitData);
      }

      onSuccess();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || error.message || 'Failed to save problem');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        onEntered: () => setEditorReady(true),
        onExited: () => setEditorReady(false)
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {isEditMode ? 'Edit Pool Problem' : 'Create New Pool Problem'}
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Title */}
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title (optional)"
                  fullWidth
                  helperText="A short title to help identify this problem"
                />
              )}
            />

            {/* Problem Statement (Rich Text Editor) */}
            <Box>
              <Typography
                variant="body2"
                color={errors.problemStatement ? 'error' : 'text.secondary'}
                sx={{ mb: 1 }}
              >
                Problem Statement *
              </Typography>
              <Box sx={{
                '& .ql-editor': { minHeight: '6em', fontSize: '16px' },
                '& .ql-container': { fontSize: '16px' }
              }}>
                {editorReady ? (
                  <ReactQuill
                    theme="snow"
                    value={statementHtml}
                    onChange={handleStatementChange}
                    modules={{
                      toolbar: [
                        ['bold', 'italic', 'underline'],
                        [{ 'script': 'sub' }, { 'script': 'super' }],
                        ['clean']
                      ]
                    }}
                    formats={['bold', 'italic', 'underline', 'script']}
                    placeholder="Enter problem statement..."
                  />
                ) : (
                  <Box sx={{
                    minHeight: '6em',
                    border: '1px solid rgba(0,0,0,0.23)',
                    borderRadius: 1,
                    p: 1
                  }} />
                )}
              </Box>
              {errors.problemStatement && (
                <FormHelperText error>{errors.problemStatement.message}</FormHelperText>
              )}
            </Box>

            {/* Problem Image */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Problem Image (optional)
              </Typography>

              <input
                ref={fileInputRef}
                accept="image/*"
                type="file"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Image'}
                </Button>

                {previewUrl && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleRemoveImage}
                    disabled={uploading}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}

                {uploading && <CircularProgress size={20} />}
              </Box>

              {uploadError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {uploadError}
                </Alert>
              )}

              {previewUrl && (
                <Card sx={{ maxWidth: 400 }}>
                  <CardMedia
                    component="img"
                    image={previewUrl}
                    alt="Problem image preview"
                    sx={{ maxHeight: 250, objectFit: 'contain' }}
                    onError={() => {
                      setUploadError('Failed to load image preview. The image may have been deleted from storage.');
                    }}
                  />
                </Card>
              )}
            </Box>

            {/* Problem Type Switch */}
            <FormControl>
              <Controller
                name="problemType"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value === 'multiple_choice'}
                        onChange={(e) =>
                          field.onChange(e.target.checked ? 'multiple_choice' : 'open_ended')
                        }
                      />
                    }
                    label={`Problem Type: ${field.value === 'multiple_choice' ? 'Multiple Choice' : 'Open Ended'}`}
                  />
                )}
              />
            </FormControl>

            {/* Open Ended: Correct Solution */}
            {problemType === 'open_ended' && (
              <Controller
                name="correctSolution"
                control={control}
                rules={{
                  required: problemType === 'open_ended' ? 'Correct solution is required' : false
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Correct Solution"
                    fullWidth
                    required={problemType === 'open_ended'}
                    error={!!errors.correctSolution}
                    helperText={errors.correctSolution?.message}
                  />
                )}
              />
            )}

            {/* Multiple Choice: Options */}
            {problemType === 'multiple_choice' && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Answer Options (select the correct one)
                </Typography>
                {optionFields.map((field, index) => (
                  <Box
                    key={field.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                  >
                    <Controller
                      name={`options.${index}.isCorrect`}
                      control={control}
                      render={({ field: checkboxField }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={checkboxField.value}
                              onChange={() => handleCorrectOptionChange(index)}
                              color="success"
                            />
                          }
                          label=""
                        />
                      )}
                    />
                    <Controller
                      name={`options.${index}.optionText`}
                      control={control}
                      render={({ field: textField }) => (
                        <TextField
                          {...textField}
                          label={`Option ${index + 1}`}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                    {optionFields.length > 2 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeOption(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {optionFields.length < 6 && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      appendOption({
                        optionText: '',
                        isCorrect: false,
                        optionOrder: optionFields.length + 1
                      })
                    }
                  >
                    Add Option
                  </Button>
                )}
              </Box>
            )}

            {/* Difficulty Level */}
            <FormControl>
              <Typography gutterBottom>Difficulty Level</Typography>
              <Controller
                name="difficultyLevel"
                control={control}
                render={({ field }) => (
                  <Slider
                    {...field}
                    min={1}
                    max={5}
                    step={1}
                    marks={[
                      { value: 1, label: 'Easy' },
                      { value: 2, label: 'Medium' },
                      { value: 3, label: 'Challenging' },
                      { value: 4, label: 'Hard' },
                      { value: 5, label: 'Expert' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                )}
              />
            </FormControl>

            {/* Category */}
            <Controller
              name="categoryId"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select {...field} label="Category">
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {categories.map(category => (
                      <MenuItem key={category.id} value={category.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: category.color
                            }}
                          />
                          {category.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            {/* Tags */}
            <FormControl>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {tags.map(tag => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    variant={selectedTagIds?.includes(tag.id) ? 'filled' : 'outlined'}
                    onClick={() => handleTagToggle(tag.id)}
                    sx={{
                      bgcolor: selectedTagIds?.includes(tag.id) ? tag.color : 'transparent',
                      color: selectedTagIds?.includes(tag.id) ? 'white' : 'inherit',
                      borderColor: tag.color,
                      '&:hover': {
                        bgcolor: selectedTagIds?.includes(tag.id) ? tag.color : `${tag.color}20`
                      }
                    }}
                  />
                ))}
              </Box>
              <FormHelperText>Click to select/deselect tags</FormHelperText>
            </FormControl>

            {/* Source */}
            <Controller
              name="source"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Source (optional)"
                  fullWidth
                  helperText="e.g., AMC 2023 #15, Original, Competition Prep Book"
                />
              )}
            />

            {/* Notes */}
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Internal Notes (optional)"
                  multiline
                  rows={2}
                  fullWidth
                  helperText="Notes for teachers (not shown to students)"
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProblemPoolFormDialog;
